import {createRouter, createWebHistory} from 'vue-router'


// Guest Routes
import Sso from "@/pages/Auth/Sso.vue"
import Login from "@/pages/Auth/Login.vue"
import Register from "@/pages/Auth/Register.vue"
import PhoneEntry from "@/pages/Auth/PhoneEntry.vue"
import PhoneOtp from "@/pages/Auth/PhoneOtp.vue"
import ForceChangePassword from "@/pages/Auth/ForceChangePassword.vue"
import VerifyEmail from "@/pages/Auth/VerifyEmail.vue"
import ResetPassword from '@/pages/Auth/ResetPassword.vue'
import ForgotPassword from '@/pages/Auth/ForgotPassword.vue'

// Auth Routes
import LiveView from "@/pages/Live.vue"
import Device from "@/pages/Devices.vue"
import Profile from "@/pages/Profile.vue"
import Members from "@/pages/Members.vue"
import Invoices from "@/pages/Invoices.vue"
import Settings from "@/pages/Settings.vue"
import Logout from "@/pages/Auth/Logout.vue"
import Monitoring from "@/pages/Monitoring.vue"
import Places from "@/pages/Places.vue"
import Activity from "@/pages/Activity.vue"
import MileageLog from "@/pages/MileageLog.vue"
import PaymentMethods from "@/pages/PaymentMethods.vue"
import PlanUpgrade from "@/pages/PlanUpgrade.vue"

const routes = [
    {
        path: "/",
        redirect: (to) => {
            return {path: "/login"};
        },
    },
    {
        path: "/about",
        name: "about",
        // route level code-splitting
        // this generates a separate chunk (about.[hash].js) for this route
        // which is lazy-loaded when the route is visited.
        component: () =>
            import(/* webpackChunkName: "about" */ "../views/AboutView.vue"),
    },
    {
        path: "/login",
        name: "login",
        meta: {
            layout: "guest",
        },
        component: Login,
    },
    {
        path: "/register/:token?",
        name: "register",
        meta: {
            layout: "guest",
        },
        component: Register,
    },
    {
        // Phone-first signup step 1: enter phone + CAPTCHA.
        // `/signup` is chosen instead of overloading `/register` so the
        // existing invitation flow (`/register/:token`) keeps working
        // untouched - invitees skip the phone step.
        path: "/signup",
        name: "signup-phone",
        meta: {layout: "guest"},
        component: PhoneEntry,
    },
    {
        // Phone-first signup step 2: enter 6-digit OTP → verification_token.
        // Caller must provide ?phone=+1… ; PhoneOtp redirects back to
        // /signup if phone is missing.
        path: "/signup/verify",
        name: "phone-otp",
        meta: {layout: "guest"},
        component: PhoneOtp,
    },
    {
        name: "sso",
        path: "/sso",
        component: Sso,
    },
    {
        // Public, no-login live-location share page. 'public' layout = no auth,
        // no chrome, no redirect (see App.vue + the beforeEach guard).
        name: "share",
        path: "/share/:token",
        meta: {layout: "public", title: "Live location"},
        component: () => import("@/pages/ShareView.vue"),
    },
    {
        // Reached from the iOS app, which cannot take payment for an account
        // plan under App Store guideline 3.1.1. Public layout so it carries no
        // sidebar or nav: a single-task page, not the app. Auth comes from the
        // one-time handoff token in the query string.
        name: "billing-info",
        path: "/billing_info",
        meta: {layout: "public", title: "SimpliGPS Billing Info Update"},
        component: () => import("@/pages/BillingInfo.vue"),
    },
    {
        // Minors are app-only. Public layout: they get a message and an OK,
        // not the app shell behind a dialog.
        name: "minor-not-supported",
        path: "/app-only",
        meta: {layout: "public", title: "Use the SimpliGPS app"},
        component: () => import("@/pages/MinorNotSupported.vue"),
    },
    {
        path: "/forgot-password",
        name: "forgot-password",
        meta: {
            layout: "guest",
        },
        component: ForgotPassword,
    },
    {
        path: "/reset-password/:id",
        name: "reset-password",
        meta: {
            layout: "guest",
        },
        component: ResetPassword,
    },
    {
        path: "/verify-email/:user",
        name: "verify-email",
        meta: {
            layout: "guest",
        },
        component: VerifyEmail,
    },
    {
        path: "/force-change-password",
        name: "force-change-password",
        meta: {
            layout: "guest",
        },
        component: ForceChangePassword,
    },
    {
        path: "/dashboard",
        name: "dashboard",
        meta: {
            layout: "auth",
        },
        component: LiveView,
    },
    {
        path: "/profile",
        name: "profile",
        meta: {
            layout: "auth",
        },
        component: Profile,
    },
    {
        path: "/devices",
        name: "devices",
        meta: {
            layout: "auth",
        },
        component: Device,
    },
    {
        // Notifications were merged into the unified Activity feed. Keep the path
        // as a redirect so old links, the top-bar bell and deep-links still land.
        path: "/notifications",
        redirect: {name: "activity"},
    },
    {
        path: "/activity",
        name: "activity",
        meta: {
            layout: "auth",
        },
        component: Activity,
    },
    {
        path: "/monitoring",
        name: "monitoring",
        meta: {
            layout: "auth",
        },
        component: Monitoring,
    },
    {
        path: "/mileage-log",
        name: "mileage",
        meta: {
            layout: "auth",
        },
        component: MileageLog,
    },
    {
        path: "/places",
        name: "places",
        meta: {
            layout: "auth",
        },
        component: Places,
    },
    {
        path: "/plans",
        name: "plans",
        meta: {
            layout: "auth",
            ownerOnly: true,
        },
        component: PlanUpgrade,
    },
    {
        path: "/payment-methods",
        name: "payment-methods",
        meta: {
            layout: "auth",
            // Billing belongs to whoever pays for the circle. Enforced in
            // beforeEach below; the API refuses these routes regardless, this
            // just stops a non-owner being shown a page that only 403s.
            ownerOnly: true,
        },
        component: PaymentMethods,
    },
    {
        path: "/invoices",
        name: "invoices",
        meta:
            {
                layout: "auth",
                ownerOnly: true,
            },
        component: Invoices,
    },
    {
        path: "/settings",
        name: "settings",
        meta:
            {
                layout: "auth",
            },
        component: Settings,
    },
    {
        path: "/members",
        name: "members",
        meta:
            {
                layout: "auth",
            },
        component: Members,
    },
    {
        path: "/logout",
        name: "logout",
        component: Logout,
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes
});
router.beforeEach(async (to, from, next) => {
    document.title = to.meta.title || process.env.VUE_APP_TITLE;

    // Auth guard for authenticated-layout routes. Before this guard
    // existed, unauth users hitting /dashboard or /devices loaded the
    // page shell and fired XHRs that 401'd - a flash of an empty
    // authed page plus noisy 401s in the server logs. Redirect to
    // login before any resource loads.
    if (to.meta.layout === 'auth') {
        try {
            const {useAuthStore} = await import('@/stores/authStore');
            const authStore = useAuthStore();
            if (!authStore.isAuthenticated()) {
                return next({name: 'login', query: {redirect: to.fullPath}});
            }
        } catch {
            // Import failed - let navigation through; the page will
            // surface whatever caused the break.
        }
    }

    // Minors do not use the website. The sidebar already hides most of it from
    // them, but hiding menu items is not the same as blocking access - a
    // reload or a typed URL walked straight in. Trap every navigation the same
    // way the password gate below does, so a fresh login, a refresh and a deep
    // link all land in the same place.
    try {
        const {useAuthStore} = await import("@/stores/authStore");
        const authStore = useAuthStore();
        if (authStore.isAuthenticated() && authStore.isMinor) {
            const allowed = new Set(["minor-not-supported", "logout"]);
            if (!allowed.has(to.name) && to.meta.layout !== "public") {
                return next({name: "minor-not-supported"});
            }
        }

        // Billing screens are the circle owner's. The three of them had NO
        // guard at all - only PlanUpgrade carried a bespoke onMounted redirect,
        // so /invoices and /payment-methods were reachable by any member and
        // simply 403'd once the API said no.
        //
        // Guarded on a LOADED user, like the PlanUpgrade check it replaces: an
        // unhydrated store must never bounce a genuine owner on a hard refresh.
        if (authStore.isAuthenticated() && to.meta.ownerOnly
            && authStore.user && !authStore.isCircleOwner) {
            // SAY WHY. Bouncing silently to Settings reads as a broken button,
            // so the member clicks it again - and several CTAs that land here
            // are shown to non-owners, including the overdue payment bar. The
            // worst version of that is a member being told the account is
            // overdue, tapping through, and landing somewhere unrelated with no
            // idea that the owner is the one who has to act. The debt then goes
            // uncollected because nobody told the right person.
            try {
                const {useSnackbarStore} = await import("@/stores/snackBarStore");
                useSnackbarStore().showSnackbar(
                    "Billing is managed by the circle owner. Ask them to take a look.",
                    "info",
                    5000,
                );
            } catch {
                // The redirect still has to happen even if the toast cannot.
            }

            return next({name: "settings"});
        }
    } catch {
        // Import failure falls through; this is a UX gate, and the API still
        // enforces what a minor may actually read or change.
    }

    // Forced password change: the server flagged this session's password as
    // admin-reset. The user is authenticated but must not reach any app screen
    // until they change it. Login.vue routes here after login, but a reload or
    // a direct URL would otherwise slip through, so trap every navigation and
    // send it back to the change-password screen. Logout stays reachable so the
    // user can back out; public (share) routes are never gated.
    try {
        const {useAuthStore} = await import('@/stores/authStore');
        const authStore = useAuthStore();
        if (authStore.isAuthenticated() && authStore.mustChangePassword) {
            const allowed = new Set(['force-change-password', 'logout']);
            if (!allowed.has(to.name) && to.meta.layout !== 'public') {
                return next({name: 'force-change-password'});
            }
        }
    } catch {
        // Fall through on import failure. This client trap is only a UX gate;
        // the real enforcement is server-side (the API rejects a reset account's
        // requests until the password is changed).
    }

    // Authenticated users shouldn't re-enter login / signup / forgot /
    // phone-entry / verify-email screens unless they've logged out first.
    if (to.meta.layout === 'guest') {
        try {
            const {useAuthStore} = await import('@/stores/authStore');
            const authStore = useAuthStore();
            const guestGuardedNames = new Set([
                'login', 'signup-phone', 'phone-otp', 'register',
                'forgot-password', 'reset-password', 'verify-email',
            ]);
            // Exception: an invitation link (/register/:token) must work even
            // when already logged in, so an existing user can accept and join.
            const isInviteAccept = to.name === 'register' && to.params.token;
            if (authStore.isAuthenticated() && guestGuardedNames.has(to.name) && !isInviteAccept) {
                return next({name: 'dashboard'});
            }
        } catch {
            // Fall through on import failure.
        }
    }

    next();
});

export default router
